import { useState, useRef, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Sidebar from "../components/Sidebar";
import api from "../api";
import { showError, showSuccess } from "../components/AppToast";
import { Edit2, Eye, EyeOff, RefreshCw } from "lucide-react";

export default function MerchantConfig() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [form, setForm] = useState({
    merchantId: "",
    productCode: "",
    reqKey: "",
    respKey: "",
    reqHashKey: "",
    resHashKey: "",
    password: "",
  });

  const [viewForm, setViewForm] = useState({
    merchantId: "",
    productCode: "",
    reqKey: "",
    respKey: "",
    reqHashKey: "",
    resHashKey: "",
    password: "",
    updatedAt: ""
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [exists, setExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const debounceRef = useRef(null);

  useEffect(() => {
    fetchMerchantConfigs();
  }, []);

  // ─── Fetch all merchant configs ───────────────────────────────────────────
  const fetchMerchantConfigs = async () => {
    setLoading(true);
    setFetchError("");
    try {
      const token = localStorage.getItem("jwt");
      const { data } = await api.post("/merchants/fechMerchantConfigs",{}, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setMerchants(Array.isArray(data) ? data.reverse() : []);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to load merchant configurations";
      setFetchError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ─── Modal helpers ────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setForm({
      merchantId: "",
      productCode: "",
      reqKey: "",
      respKey: "",
      reqHashKey: "",
      resHashKey: "",
      password: "",
    });
    setErrors({});
    setTouched({});
    setExists(false);
    setIsEditMode(false);
    setCreateEditModalOpen(true);
  };

  const openEditModal = (row) => {
    setForm({
      merchantId: row.merchantId || "",
      productCode: row.productCode || "",
      reqKey: row.reqKey || "",
      respKey: row.respKey || "",
      reqHashKey: row.reqHashKey || "",
      resHashKey: row.resHashKey || "",
      password: row.password || "",
    });
    setErrors({});
    setTouched({});
    setExists(false);
    setIsEditMode(true);
    setCreateEditModalOpen(true);
  };

  const openViewModal = (row) => {
    setViewForm({
      merchantId: row.merchantId || "",
      productCode: row.productCode || "",
      reqKey: row.reqKey || "",
      respKey: row.respKey || "",
      reqHashKey: row.reqHashKey || "",
      resHashKey: row.resHashKey || "",
      password: row.password || "",
      updatedAt: row.updatedAt || "N/A"
    });
    setViewModalOpen(true);
  };

  const closeCreateEditModal = () => {
    setCreateEditModalOpen(false);
    setShowPassword(false);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
  };

  // ─── Field-aware validation ───────────────────────────────────────────────
  const FIELD_LABELS = {
    merchantId: "Merchant ID",
    productCode: "Product Code",
    reqKey: "Request Key",
    respKey: "Response Key",
    reqHashKey: "Request Hash Key",
    resHashKey: "Response Hash Key",
    password: "Password",
  };

  const validateField = (name, value) => {
    const label = FIELD_LABELS[name] || name;

    if (!value?.trim()) return `${label} is required`;

    if (name === "merchantId" && !/^\d+$/.test(value.trim()))
      return "Merchant ID must contain numbers only";



    if (
      ["reqKey", "respKey", "reqHashKey", "resHashKey"].includes(name) &&
      !/^[a-zA-Z0-9]+$/.test(value.trim())
    )
      return `${label} must be alphanumeric`;

    // password: all characters allowed
    return "";
  };

  const renderFeedback = (name) => (
    <>
      {touched[name] && errors[name] && (
        <FormFeedback>{errors[name]}</FormFeedback>
      )}
    
    </>
  );

  // ─── Merchant ID existence check (/exists only — no detail fetch) ─────────
  const checkMerchantIdExists = async (merchantId) => {
    if (!merchantId?.trim() || isEditMode) return;

    try {
      const token = localStorage.getItem("jwt");
      const { data } = await api.post(
        `/merchants/exists?merchantId=${encodeURIComponent(merchantId)}`,{},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (data?.exists) {
        setExists(true);
        showError(
          "This Merchant ID already exists. Please use a different ID or edit the existing record from the table."
        );
      } else {
        setExists(false);
      }
    } catch {
      // Silently ignore — don't block the form on lookup failure
      setExists(false);
    }
  };

  // ─── Sanitize input on the fly ───────────────────────────────────────────
  const sanitizeValue = (name, value) => {
    if (name === "merchantId")
      return value.replace(/[^0-9]/g, "");              // numbers only
         // alphabets only
    if (["reqKey", "respKey", "reqHashKey", "resHashKey"].includes(name))
      return value.replace(/[^a-zA-Z0-9]/g, "");        // alphanumeric only
    return value;                                         // password: all chars
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = sanitizeValue(name, value);

    setForm((prev) => ({ ...prev, [name]: sanitized }));

    const err = validateField(name, sanitized);
    setErrors((prev) => ({ ...prev, [name]: err }));

    if (name === "merchantId" && !err && !isEditMode) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        checkMerchantIdExists(sanitized.trim());
      }, 600);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: err }));

    if (name === "merchantId" && !err && !isEditMode) {
      checkMerchantIdExists(value.trim());
    }
  };

  const isFormValid = () =>
    !Object.values(errors).some(Boolean) &&
    !(exists && !isEditMode) &&
    Object.keys(FIELD_LABELS).every((f) => !!form[f]?.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Touch all fields to surface errors
    const allTouched = Object.keys(FIELD_LABELS).reduce(
      (acc, f) => ({ ...acc, [f]: true }),
      {}
    );
    setTouched(allTouched);

    const allErrors = Object.keys(FIELD_LABELS).reduce(
      (acc, f) => ({ ...acc, [f]: validateField(f, form[f]) }),
      {}
    );
    setErrors(allErrors);

    if (Object.values(allErrors).some(Boolean)) return;
    if (exists && !isEditMode) {
      return showError(
        "Merchant ID already exists. Please use a different ID."
      );
    }

    const payload = { ...form, merchantId: form.merchantId.trim() };

    setSubmitting(true);
    try {
      const token = localStorage.getItem("jwt");

      if (isEditMode) {
        await api.post("/merchants/update", payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        showSuccess("Merchant configuration updated successfully.");
      } else {
        await api.post("/merchants/create", payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        showSuccess("Merchant configuration created successfully.");
      }

      closeCreateEditModal();
      fetchMerchantConfigs();
    } catch (err) {
      showError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          "Save failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Sr No",
      sortable: false,
      width: "80px",
      center: true,
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
    },
    {
      name: "Merchant ID",
      selector: (row) => row.merchantId,
      sortable: true,
    },
    {
      name: "Product Code",
      selector: (row) => row.productCode,
      sortable: true,
    },
    {
      name: "Request Key",
      selector: (row) =>
        row.reqKey ? row.reqKey.substring(0, 12) + "..." : "-",
      sortable: false,
    },
    {
      name: "Response Key",
      selector: (row) =>
        row.respKey ? row.respKey.substring(0, 12) + "..." : "-",
      sortable: false,
    },
    {
      name: "Req Hash Key",
      selector: (row) => row.reqHashKey || "-",
      sortable: false,
    },
    {
      name: "Res Hash Key",
      selector: (row) => row.resHashKey || "-",
      sortable: false,
    },
    {
      name: "Actions",
      width: "140px",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            color="primary"
            size="sm"
            outline
            onClick={() => openEditModal(row)}
            title="Edit"
          >
            <Edit2 size={16} />
          </Button>
          <Button
            color="primary"
            size="sm"
            outline
            onClick={() => openViewModal(row)}
            title="View"
          >
            <Eye size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    },
  ];

  // ─── Field definitions ────────────────────────────────────────────────────
  const createEditFields = [
    {
      name: "merchantId",
      label: "Merchant ID *",
      placeholder: "Enter Merchant ID",
      inputMode: "numeric",
      disabled: isEditMode,
    },
    {
      name: "productCode",
      label: "Product Code *",
      placeholder: "Enter Product Code",
      inputMode: "text",
    },
    {
      name: "reqKey",
      label: "Request Key *",
      placeholder: "Enter Request Key",
      inputMode: "text",
    },
    {
      name: "respKey",
      label: "Response Key *",
      placeholder: "Enter Response Key",
      inputMode: "text",
    },
    {
      name: "reqHashKey",
      label: "Request Hash Key *",
      placeholder: "Enter Request Hash Key",
      inputMode: "text",
    },
    {
      name: "resHashKey",
      label: "Response Hash Key *",
      placeholder: "Enter Response Hash Key",
      inputMode: "text",
    },
    {
      name: "password",
      label: "Password *",
      placeholder: "Enter Password",
      inputMode: "text",
    },
  ];

  const viewFields = [
    { name: "merchantId",  label: "Merchant ID" },
    { name: "productCode", label: "Product Code" },
    { name: "reqKey",      label: "Request Key" },
    { name: "respKey",     label: "Response Key" },
    { name: "reqHashKey",  label: "Request Hash Key" },
    { name: "resHashKey",  label: "Response Hash Key" },
    { name: "password",    label: "Password" },
    {name: "updatedAt", label: "Updated At"},
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="px-shell d-flex">
      <Sidebar />

      <main className="px-main">
        <div className="px-page-head mb-4">
          <h2 className="px-title">Merchant Configurations</h2>
          <div className="px-sub">
            Manage payment gateway merchant credentials and keys
          </div>
        </div>

        <Container fluid className="p-0">
          <Row className="align-items-center mb-3">
            <Col className="d-flex gap-2 align-items-center flex-wrap" />

            <Col md="auto" className="ms-auto d-flex gap-2">
              <Button
                color="outline-secondary"
                onClick={fetchMerchantConfigs}
                disabled={loading}
              >
                <RefreshCw size={16} className="me-2" />
                {loading ? "Refreshing..." : "Refresh"}
              </Button>

              <Button color="primary" onClick={openCreateModal}>
                + Create Merchant
              </Button>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card className="px-card borderless-card">
                <CardBody className="borderless-card-body">
                  <div className="px-card-head mb-3">
                    <h4>Merchant Configurations</h4>
                    <span className="px-muted">
                      Showing {merchants.length} records
                    </span>
                  </div>

                  <DataTable
                    columns={columns}
                    data={merchants}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={(page) => setCurrentPage(page)}
                    onChangeRowsPerPage={(newPerPage) => {
                      setRowsPerPage(newPerPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    responsive
                    progressPending={loading}
                    noDataComponent={
                      <div style={{ padding: 40, color: "#64748b" }}>
                        {fetchError || "No merchant configurations found"}
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* ── Create / Edit Modal ── */}
        <Modal
          isOpen={createEditModalOpen}
          toggle={closeCreateEditModal}
          size="xl"
          centered
          style={{ maxWidth: "900px" }}
        >
          <ModalHeader
            toggle={closeCreateEditModal}
            className="border-0 pb-0 pt-4 px-5"
          >
            <h3 className="mb-0">
              {isEditMode
                ? "Edit Merchant Configuration"
                : "Create New Merchant"}
            </h3>
          </ModalHeader>

          <ModalBody className="pt-4 px-5 pb-4">
            <form onSubmit={handleSubmit}>
              <Row className="g-4">
                {createEditFields.map((field) => (
                  <Col md={6} key={field.name}>
                    <FormGroup className="mb-0">
                      <Label
                        className="fw-medium mb-2"
                        style={{ fontSize: "0.95rem" }}
                      >
                        {field.label}
                      </Label>

                      {field.name === "password" ? (
                        <>
                          <div className="input-group">
                            <Input
                              type={showPassword ? "text" : "password"}
                              name="password"
                              value={form.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.password && !!errors.password}
                              placeholder={field.placeholder}
                              style={{
                                padding: "0.65rem 0.75rem",
                                fontSize: "0.95rem",
                              }}
                              maxLength={50}
                            />
                            <Button
                              type="button"
                              color="outline-secondary"
                              onClick={() => setShowPassword((p) => !p)}
                              style={{ padding: "0.65rem 1rem" }}
                            >
                              {showPassword ? (
                                <EyeOff size={18} />
                              ) : (
                                <Eye size={18} />
                              )}
                            </Button>
                          </div>
                          {touched.password && errors.password && (
                            <div
                              className="text-danger mt-1"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {errors.password}
                            </div>
                          )}
                          
                        </>
                      ) : (
                        <Input
                          name={field.name}
                          value={form[field.name]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={touched[field.name] && !!errors[field.name]}
                          valid={
                            touched[field.name] &&
                            !errors[field.name] &&
                            !!form[field.name]?.trim()
                          }
                          disabled={field.disabled}
                          placeholder={field.placeholder}
                          inputMode={field.inputMode}
                          style={{
                            padding: "0.65rem 0.75rem",
                            fontSize: "0.95rem",
                            cursor: field.disabled ? "not-allowed" : "default",
                          }}
                          maxLength={50}
                        />
                      )}

                      {field.name !== "password" && renderFeedback(field.name)}

                      {/* Merchant ID availability badge */}
                      {field.name === "merchantId" &&
                        !isEditMode &&
                        form.merchantId && (
                          <small
                            className={`d-block mt-2 ${
                              exists ? "text-danger" : "text-success"
                            }`}
                            style={{ fontSize: "0.875rem" }}
                          >
                            {exists
                              ? "⚠ Merchant ID already exists — use a different ID or edit from the table"
                              : "✓ Merchant ID is available"}
                          </small>
                        )}
                    </FormGroup>
                  </Col>
                ))}
              </Row>
            </form>
          </ModalBody>

          <ModalFooter className="border-0 pt-3 pb-4 px-5">
            <Button
              color="light"
              onClick={closeCreateEditModal}
              style={{ padding: "0.65rem 1.5rem", fontSize: "0.95rem" }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={!isFormValid() || submitting}
              style={{
                minWidth: "160px",
                padding: "0.65rem 1.5rem",
                fontSize: "0.95rem",
                border: "none",
              }}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : isEditMode ? (
                "Update Merchant"
              ) : (
                "Create Merchant"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── View Modal ── */}
        <Modal
          isOpen={viewModalOpen}
          toggle={closeViewModal}
          size="xl"
          centered
          style={{ maxWidth: "900px" }}
        >
          <ModalHeader
            toggle={closeViewModal}
            className="border-0 pb-0 pt-4 px-5"
          >
            <h3 className="mb-0">View Merchant Configuration</h3>
          </ModalHeader>

          <ModalBody className="pt-4 px-5 pb-4">
            <Row className="g-4">
              {viewFields.map((field) => (
                <Col md={6} key={field.name}>
                  <FormGroup className="mb-0">
                    <Label
                      className="fw-medium mb-2"
                      style={{ fontSize: "0.95rem" }}
                    >
                      {field.label}
                    </Label>
                    <Input
                      value={
                        field.name === "password"
                          ? "••••••••••••"
                          : viewForm[field.name]
                      }
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </ModalBody>

          <ModalFooter className="border-0 pt-3 pb-4 px-5">
            <Button
              color="primary"
              onClick={closeViewModal}
              style={{ padding: "0.65rem 1.5rem", fontSize: "0.95rem" }}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </main>
    </div>
  );
}
